import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import api, { API } from "../lib/api";
import { X, Loader2, Save, Building2, Upload, Trash2 } from "lucide-react";
import { formatApiError } from "../lib/errors";

const ProfileModal = ({ open, doctor, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: "", clinic_name: "", clinic_address: "", phone: "", prescription_footer_note: "",
  });
  const [logo, setLogo] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && doctor) {
      setForm({
        name: doctor.name || "",
        clinic_name: doctor.clinic_name || "",
        clinic_address: doctor.clinic_address || "",
        phone: doctor.phone || "",
        prescription_footer_note: doctor.prescription_footer_note || "",
      });
      setLogo(doctor.clinic_logo || "");
      setErr("");
    }
  }, [open, doctor]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await api.put("/auth/profile", form);
      onSaved?.(res.data.doctor);
      onClose();
    } catch (e2) {
      setErr(formatApiError(e2, "Update failed"));
    } finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) {
      setErr("Logo must be under 500 KB. Please compress the image.");
      return;
    }
    setErr(""); setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("dotmedy_token");
      const res = await axios.post(`${API}/auth/clinic-logo`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setLogo(res.data.clinic_logo);
      // refresh parent doctor object
      const me = await api.get("/auth/me");
      onSaved?.(me.data.doctor);
    } catch (e2) {
      setErr(formatApiError(e2, "Logo upload failed"));
    } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove clinic logo?")) return;
    setLogoBusy(true);
    try {
      await api.delete("/auth/clinic-logo");
      setLogo("");
      const me = await api.get("/auth/me");
      onSaved?.(me.data.doctor);
    } catch { alert("Failed"); } finally { setLogoBusy(false); }
  };

  const fld = (label, key, type = "text", placeholder = "") => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Building2 size={18} className="text-[#14B8A6]" />
            <h3 className="font-semibold text-slate-800">Clinic Details</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-3">
          <p className="text-sm text-slate-600">Shown on invoices, examinations, treatment plans and letterheads.</p>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Clinic Logo</label>
            <div className="flex items-center gap-3 border border-slate-200 rounded-md p-3 bg-slate-50">
              <div className="w-16 h-16 rounded bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                {logo ? <img src={logo} alt="Logo" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-slate-400">No logo</span>}
              </div>
              <div className="flex-1 flex gap-2 flex-wrap">
                <input ref={fileRef} type="file" accept="image/jpeg,image/png" onChange={(e) => uploadLogo(e.target.files?.[0])} className="hidden" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={logoBusy} className="text-xs inline-flex items-center gap-1 bg-[#14B8A6] hover:bg-[#0F8A7D] disabled:opacity-60 text-white px-3 py-1.5 rounded">
                  {logoBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {logo ? "Change" : "Upload logo"}
                </button>
                {logo && (
                  <button type="button" onClick={removeLogo} disabled={logoBusy} className="text-xs inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-100 text-slate-600 px-3 py-1.5 rounded">
                    <Trash2 size={12} /> Remove
                  </button>
                )}
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">JPG or PNG • max 500 KB</p>
          </div>

          {fld("Doctor's Name", "name", "text", "Dr. Anaya Sharma")}
          {fld("Clinic Name", "clinic_name", "text", "Sunrise Dental")}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Clinic Address</label>
            <textarea
              rows={2}
              value={form.clinic_address}
              onChange={(e) => setForm((f) => ({ ...f, clinic_address: e.target.value }))}
              placeholder="69FF, Zirakpur, Mohali, Punjab"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
            />
          </div>
          {fld("Phone Number", "phone", "tel", "+91 99884 46478")}

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Prescription Footer Note</label>
            <textarea
              rows={3}
              value={form.prescription_footer_note}
              onChange={(e) => setForm((f) => ({ ...f, prescription_footer_note: e.target.value }))}
              placeholder="e.g. Diwali offer: 20% off on scaling this month. Emergency? Call +91 99884 46478."
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
            />
            <p className="text-[10px] text-slate-400 mt-1">Appears on every letterhead, examination and treatment-plan print.</p>
          </div>

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
            <button type="submit" disabled={busy} className="px-4 py-2 text-sm bg-[#14B8A6] hover:bg-[#0F8A7D] disabled:opacity-70 text-white rounded-md font-medium inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileModal;
